/* eslint-disable react/jsx-props-no-spreading */
/**
 * @description Renders the individual list items for the Personnel List
 */

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import PropTypes from 'prop-types';

import SelectBox from './SelectBox';
import DropdownField from './DropdownField';
import DateInputField from './DateInputField';
import { STATUSES } from '../constants';
import useAccessRequiredOptions from '../hooks/useAccessRequiredOptions';
import { ContractInfoContext } from '../contexts/contractInfo';

const styles = {
  personnelContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 2,
    width: '100%',
    '& > .personnel-settings': {
      flex: 1
    }
  },
  personnelInfo: {
    display: 'flex',
    gap: 2,
    width: '100%'
  },
  personnelSettings: {
    display: 'flex',
    gap: 2,
    justifyContent: 'stretch',
    width: '100%',
    '& > *': {
      flex: 1
    }
  }
};

/**
 * @param {object} person - a personnel entity
 * @param {number} index - the index of the personnel entity
 */
const PersonnelListItem = ({ person, index }) => {
  const accessRequiredOptions = useAccessRequiredOptions();
  const contract = React.useContext(ContractInfoContext);
  const [dateAssignedRequired, setDateAssignedRequired] = useState(false);

  const { watch, setValue } = useFormContext();

  const eligibilityValue = watch(`personnel.${ index }.eligibility`);
  const statusValue = watch(`personnel.${ index }.status`);

  // checks if the list item has a eligibility value and clears the accessRequired field if it doesn't
  useEffect(() => {
    if (!eligibilityValue) {
      setValue(`personnel.${ index }.accessRequired`, undefined);
    }
  }, [eligibilityValue]);

  // Toggle the date assignment required field based on if the status value is assigned or pending assigned
  useEffect(() => {
    if (!statusValue?.value) {
      return;
    }

    setDateAssignedRequired(statusValue.value === 'Assigned');
  }, [statusValue]);

  return (
    <ListItem key={person.id} sx={styles.personnelContainer}>
      <SelectBox index={index} personnelId={person.personnelId} />
      <Stack className='personnel-settings' spacing={2}>
        <Stack>
          <Typography variant='h6' component='p'>
            {person.name}
          </Typography>
          <Box sx={styles.personnelInfo}>
            <Typography variant='body2'>
              <Typography component='span' variant='body2' fontWeight='bold'>
                Facility Name:
              </Typography>
              {person.companyOrDbaNameAndFacilityCode}
            </Typography>
            <Typography variant='body2'>
              <Typography component='span' variant='body2' fontWeight='bold'>
                SSN:
              </Typography>
              {person.ssn}
            </Typography>
          </Box>
        </Stack>
        <Stack id='personnel-settings' spacing={contract && contract.showAllPersonnelAssignmentDates ? 2 : 0}>
          <Box id='personnel-default' sx={styles.personnelSettings}>
            <DropdownField
              id={`personnel.${ index }.eligibility`}
              label='Eligibility Level'
              options={person.eligibilityOptions}
            />
            <DropdownField
              id={`personnel.${ index }.accessRequired`}
              isError={accessRequiredOptions.isError}
              isLoading={accessRequiredOptions.isLoading}
              label='Access Required'
              options={accessRequiredOptions.data}
              disabled={!eligibilityValue}
            />
            <DropdownField
              id={`personnel.${ index }.specialAccesses`}
              label='Special Access'
              multiple
              options={person.specialAccessOptions}
            />
            <DropdownField
              disableClearable
              id={`personnel.${ index }.status`}
              label='Status'
              options={STATUSES}
              required
            />
            { !contract.showAllPersonnelAssignmentDates && (
            <DateInputField
              id={`personnel.${ index }.dateAssigned`}
              required={dateAssignedRequired}
              label='Date Assigned'
            />
            )}
          </Box>
          { contract.showAllPersonnelAssignmentDates && (
            <Box sx={styles.personnelSettings}>
              <DateInputField
                id={`personnel.${ index }.datePendingAssigned`}
                label='Date Pending Assigned'
              />
              <DateInputField
                id={`personnel.${ index }.dateRequested`}
                label='Date Requested'
              />
              <DateInputField
                id={`personnel.${ index }.dateSubmitted`}
                label='Date Submitted'
              />
              <DateInputField
                id={`personnel.${ index }.dateAssigned`}
                required={dateAssignedRequired}
                label='Date Assigned'
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </ListItem>
  );
};

/**
 * creates a loading Skeleton that mirrors the look of the PersonnelListItem component
 */
export const PersonnelListItemSkeleton = () => (
  <ListItem sx={styles.personnelContainer}>
    <Skeleton animation='wave' variant='rectangular' height={24} width={24} />
    <Stack className='personnel-settings' spacing={2}>
      <Stack>
        <Skeleton
          animation='wave'
          variant='rectangular'
          height={40}
          width='33.33%'
        />
      </Stack>
      <Box id='personnel-settings' sx={styles.personnelSettings}>
        <Skeleton animation='wave' variant='rectangular' height={40} />
        <Skeleton animation='wave' variant='rectangular' height={40} />
        <Skeleton animation='wave' variant='rectangular' height={40} />
        <Skeleton animation='wave' variant='rectangular' height={40} />
        <Skeleton animation='wave' variant='rectangular' height={40} />
      </Box>
    </Stack>
  </ListItem>
);

PersonnelListItem.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    personnelId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    companyOrDbaNameAndFacilityCode: PropTypes.string,
    ssn: PropTypes.string.isRequired,
    eligibilityOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    ),
    specialAccessOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      })
    )

  }).isRequired,
  index: PropTypes.number.isRequired
};

export default PersonnelListItem;
